// Copyright TraderEvolution Global LTD. © 2017-2024. All rights reserved.
//import { types } from 'node-sass';
import { DirectPropertyOwner } from '../../Utils/Enums/Constants.js';

export class RulesCache
{
    /**
     * @type { null | import('../DataCache.js').DataCache }
     */
    #DataCache;
    /**
     * @type { types }
     */
    #RulesByAccount = {};
    /**
     * @type { types }
     */
    #RulesByUser = {};
    /**
     * @type { types }
     */
    #RulesByUserGroup = {};
    /**
     * @type { types }
     */
    #FeachureFlags = {};
    /**
     * @type { types }
     */
    #GlobalRules = {};

    constructor(datacache)
    {
        this.#DataCache = datacache;
    }

    Clear()
    {
        this.#RulesByAccount = {};
        this.#RulesByUser = {};
        this.#RulesByUserGroup = {};
        this.#FeachureFlags = {};
        this.#GlobalRules = {};
    }

    /** 
    * Add rule to cache
    * @param { DirectPropertyOwner } ownerType
    * @param { string } ownerID
    * @param { string } ruleKey
    * @param { string } ruleValue
    */
    AddRule(ownerType, ownerID, ruleKey, ruleValue)
    {
        switch(ownerType)
        {
            case DirectPropertyOwner.Account:
                if(!this.#RulesByAccount.hasOwnProperty(ownerID))
                {
                    this.#RulesByAccount[ownerID] = {};
                }
                this.#RulesByAccount[ownerID][ruleKey] = ruleValue;
            break;

            case DirectPropertyOwner.User:
                if(!this.#RulesByUser.hasOwnProperty(ownerID))
                {
                    this.#RulesByUser[ownerID] = {};
                }
                this.#RulesByUser[ownerID][ruleKey] = ruleValue;
            break;

            case DirectPropertyOwner.Group:
                if(!this.#RulesByUserGroup.hasOwnProperty(ownerID))
                {
                    this.#RulesByUserGroup[ownerID] = {};
                }
                this.#RulesByUserGroup[ownerID][ruleKey] = ruleValue;
            break;

            default:
                this.#GlobalRules[ruleKey] = ruleValue;
                break;
        }
    }

    /**
    * Add feachure flag to cache
    * @param { string } ruleKey
    * @param { string } ruleValue
    */
    AddFeachureFlag(ruleKey, ruleValue)
    {
        this.#FeachureFlags[ruleKey] = ruleValue;
    }

    /**
    * Get rule value from cache
    * @param { string } ruleName
    * @param { null | import('./Account.js').Account } account
    */
    GetRuleValue(ruleName, account)
    {
        let acc = account;
        if(acc == null)
        {
            acc = this.#DataCache.MainAccountNew;
        }

        if(acc == null)
        {
            return this.#GlobalRules.hasOwnProperty(ruleName) ? this.#GlobalRules[ruleName] : null;
        }

        if(this.#RulesByAccount.hasOwnProperty(acc.BstrAccount) 
            && this.#RulesByAccount[acc.BstrAccount].hasOwnProperty(ruleName))
        {
            return this.#RulesByAccount[acc.BstrAccount][ruleName];
        }

        if(this.#RulesByUser.hasOwnProperty(acc.userID) 
            && this.#RulesByUser[acc.userID].hasOwnProperty(ruleName))
        {
            return this.#RulesByUser[acc.userID][ruleName];
        }

        let groupMessage = this.#DataCache.GetUserGroupById(acc.User.GroupId);
        while(groupMessage != null)
        {
            let groupId = groupMessage.GroupId;
            if(this.#RulesByUserGroup.hasOwnProperty(groupId) 
                && this.#RulesByUserGroup[groupId].hasOwnProperty(ruleName))
            {
                return this.#RulesByUserGroup[groupId][ruleName];
            }
            groupMessage = this.#DataCache.GetUserGroupById(groupMessage.SuperGroupId);
        }

        return this.#GlobalRules.hasOwnProperty(ruleName) ? this.#GlobalRules[ruleName] : null;
    }

    /**
    * Check if rule value equal 1
    * @param { string } ruleName
    * @param { null | import('./Account.js').Account } account
    * @returns {boolean} Is rule allowed for account?
    */
    IsAllowedForAccount(ruleName, account)
    {
        return this.GetRuleValue(ruleName, account) === "1";
    }

    /**
    * Check if feachure flag is enabled
    * @param { string } ruleName
    * @returns {boolean} Is feature flag allowed?
    */
    IsAllowedFeatureFlag(ruleName)
    {
        if(this.#FeachureFlags.hasOwnProperty(ruleName))
        {
            return this.#FeachureFlags[ruleName] == "true";
        }
        return false;
    }

    /**
    * Check rule through all accounts on user
    * @param { string } ruleName
    * @param { null | import('./Account.js').User } user
    * @returns {boolean} Is feature flag allowed?
    */
    IsAllowedForUser(ruleName, user)
    {
        if (user == null)
            return false;

        for (let i = 0; i < user.Accounts.length; i++)
        {
            if (this.IsAllowedForAccount(ruleName, user.Accounts[i]))
            {
                return true;
            }
        }
        return false;
    }

    /**
    * Check rule for main user
    * @param { string } ruleName
    * @returns {boolean} Is feature flag allowed?
    */
    IsAllowedForMyUser(name)
    {
        let dc = this.#DataCache;

        let user = dc.LoginUser;
        if (user == null)
            user = dc.getUserByLogin(dc.FLogin);

        return this.IsAllowedForUser(name, user);
    }

    /**
    * Check rule through all accounts on user
    * @param { string } ruleName
    * @param { null | import('./Account.js').User } user
    * @returns {boolean} Is feature flag allowed?
    */
    GetRuleValueForUser(ruleName, user)
    {
        if (user == null)
            return null;

        for (let i = 0; i < user.Accounts.length; i++)
        {
            let ruleValue = this.GetRuleValue(ruleName, user.Accounts[i]);
            if (ruleValue)
            {
                return ruleValue;
            }
        }
        return null;
    }

    /**
    * Check rule for main user
    * @param { string } ruleName
    * @returns {boolean} Is feature flag allowed?
    */
    GetRuleValueForMyUser(name)
    {
        let dc = this.#DataCache;

        let user = dc.LoginUser;
        if (user == null)
            user = dc.getUserByLogin(dc.FLogin);

        return this.GetRuleValueForUser(name, user);
    }
}